import { faTrain } from '@fortawesome/pro-regular-svg-icons';
import { parse } from 'qs';
import { type TrainPathData } from './types';
import TrainPriceLoader from './TrainPriceLoader';
import { TripSearchForm } from '@search/trip/TripSearchForm';
import {
    SellTypeCode,
    trainSellTypes,
    type TrainLocation,
    type TrainUrlParams,
    type SellType,
} from '@utils/train';
import {
    PaxType,
    type PaxCount,
    type ReserveInfo,
    SearchMode,
} from '@utils/types';
import { ReserveType, VerifyPathError } from '@utils/itinerary';
import { type UrlStringParams } from '@search/types';
import { TripType } from '@utils/trip';
import foreignStations from '@static/foreign_train_stations';

export class TrainSearchForm extends TripSearchForm<
    TrainLocation,
    TrainPathData
> {
    trainStations: TrainLocation[] = [];
    sellType: SellType = trainSellTypes[0];
    exclusive = false;
    override get foreignOrigins() {
        return foreignStations;
    }

    override get foreignDestinations() {
        return foreignStations;
    }

    override get domesticOrigins() {
        return this.trainStations.map(station => ({
            ...station,
            code: station.code.toString(),
        }));
    }

    override get domesticDestinations() {
        return this.trainStations.map(station => ({
            ...station,
            code: station.code.toString(),
        }));
    }

    async findCodeBaseOnStationId(
        stationId: number
    ): Promise<string | undefined> {
        await this.searchLocations(`${stationId}`);
        return this.trainStations.find(s => s.stationId === stationId)?.code;
    }

    override async verifyViewPath(
        path: string,
        pathData: TrainPathData
    ): Promise<void> {
        const fullRegex =
            /^\/trains\/(\d+)\/(\d+)\/?(\d{4}-\d{2}-\d{2})?(?:\/(\d{4}-\d{2}-\d{2}))?\/?(\d{1,2})?\/?(\d{1,2})?\/?(\d{1,2})?\/?([1-4])?\/?(\d)?$/;

        if (!fullRegex.test(path)) {
            return super.verifyViewPath(path, pathData);
        }
        const matches = path.match(fullRegex)!;
        const origin = await this.findCodeBaseOnStationId(Number(matches[1]));
        const destination = await this.findCodeBaseOnStationId(
            Number(matches[2])
        );
        const newUrl = this.generateViewUrl({
            origin,
            destination,
            departureDate: matches[3],
            returnDate: matches[4],
            adultCount: matches[5],
            childCount: matches[6],
            infantCount: matches[7],
            sellTypeCode: matches[8],
            exclusive: matches[9],
        });
        const [url, newQueries] = newUrl.split('?');
        await super.verifyViewPath(url, parse(newQueries) as TrainPathData);
        return Promise.reject(
            new VerifyPathError(
                'Path not valid',
                301,
                true,
                url,
                parse(newQueries) as { [key: string]: string }
            )
        );
    }

    override async loadViewUrlParams(params: TrainUrlParams) {
        await super.loadViewUrlParams(params);
        this.setSellTypeCode(params.sellTypeCode || SellTypeCode.Family);
        this.exclusive = params.exclusive || false;
    }

    override async searchLocations(
        searchKeyword: string,
        options: { target: 'origin' | 'destination'; mode: SearchMode } = {
            mode: SearchMode.Domestic,
            target: 'origin',
        }
    ) {
        if (!this.trainStations.length) {
            this.isLoadingLocations = true;
            this.trainStations = (
                await import('@static/domestic_train_stations')
            ).default;
            this.isLoadingLocations = false;
        }
        await super.searchLocations(searchKeyword, options);
    }

    override generateViewUrl(params: UrlStringParams<TrainUrlParams>) {
        const query = new URLSearchParams();
        if (params.departureDate) {
            query.append('departureDate', params.departureDate);
        }
        if (params.returnDate) {
            query.append('returnDate', params.returnDate);
        }
        if (params.adultCount && params.adultCount !== '1') {
            query.append('adultCount', params.adultCount);
        }
        if (params.childCount && params.childCount !== '0') {
            query.append('childCount', params.childCount);
        }
        if (params.infantCount && params.infantCount !== '0') {
            query.append('infantCount', params.infantCount);
        }
        if (params.sellTypeCode && params.sellTypeCode !== '3') {
            query.append('sellTypeCode', params.sellTypeCode);
        }
        if (params.exclusive && params.exclusive !== '0') {
            query.append('exclusive', params.exclusive);
        }
        const q = query.toString();
        return `/trains/${params.origin}-${params.destination}${
            q ? `?${q}` : ''
        }`;
    }

    override getPriceLoader(
        origin: TrainLocation,
        destination: TrainLocation,
        tripType: TripType
    ): TrainPriceLoader {
        return new TrainPriceLoader({
            tripType,
            from: `${origin.stationId}`,
            to: `${destination.stationId}`,
            adultCount: this.adultCount,
            childCount: this.childCount,
            infantCount: this.infantCount,
            priceTypeCode: `${this.sellType.code}`,
        });
    }

    override initPaxCounts: PaxCount[] = [
        {
            code: PaxType.Adult,
            title: 'بزرگسال',
            subtitle: 'بزرگتر از ۱۲ سال',
            count: 1,
        },
        {
            code: PaxType.Child,
            title: 'کودک',
            subtitle: 'بین ۲ الی ۱۲ سال',
            count: 0,
        },
    ];

    override get capacityInfo(): {
        [key in PaxType]?: {
            count: number;
            min: number;
            max: number;
        };
    } {
        return {
            [PaxType.Adult]: {
                count: this.adultCount,
                min: 1,
                max:
                    this.sellType.code === SellTypeCode.Car
                        ? 1
                        : 6 - this.childCount - this.infantCount,
            },
            [PaxType.Child]: {
                count: this.childCount,
                min: 0,
                max: 6 - this.adultCount - this.infantCount,
            },
        };
    }

    override updatePaxCount(params: { type: PaxType; count: number }) {
        const paxCount = this.capacityInfo[params.type];
        const initPaxCount = this.initPaxCounts.find(
            pax => pax.code === params.type
        );
        if (
            !initPaxCount ||
            !paxCount ||
            params.count > paxCount.max ||
            params.count < paxCount.min
        ) {
            throw new Error(
                this.sellType.code === 4
                    ? 'حداکثر تعداد برای رزرو ۱ خودرو می‌باشد.'
                    : 'بنابر دستور شرکت راه‌آهن حداکثر تعداد مسافران ۶ نفر است.'
            );
        }

        initPaxCount.count = params.count;
    }

    override popularDomesticOrigins = [
        { code: 'tehran', title: 'تهران', stationId: 1 },
        { code: 'mashhad', title: 'مشهد', stationId: 191 },
        {
            code: 'shahroud',
            title: 'شاهرود',
            stationId: 130,
        },
        { code: 'yazd', title: 'یزد', stationId: 219 },
        {
            code: 'zanjan',
            title: 'زنجان',
            stationId: 97,
        },
    ];

    override popularDomesticDestinations = this.popularDomesticOrigins;
    override popularForeignOrigins = [];
    override popularForeignDestinations = [];

    override get reserveInfo(): ReserveInfo {
        return {
            type: ReserveType.Train,
            title: 'قطار',
            nameE: 'قطاری',
            icon: faTrain,
        };
    }

    override getLocationKeywords(location: TrainLocation) {
        return [
            ...super.getLocationKeywords(location),
            `${location.stationId}`,
        ];
    }

    override get viewUrl() {
        return this.generateViewUrl({
            origin: this.origin?.code,
            destination: this.destination?.code,
            departureDate: this.formattedDepartureDate,
            returnDate:
                this.tripType === TripType.RoundTrip
                    ? this.formattedReturnDate
                    : undefined,
            adultCount: this.adultCount.toString(),
            childCount: this.childCount.toString(),
            infantCount: this.infantCount.toString(),
            sellTypeCode: this.sellType.code.toString(),
            exclusive: this.exclusive ? '1' : '0',
        });
    }

    findStationIdBaseOnCode(code: string): number | undefined {
        return this.trainStations.find(s => s.code === code)?.stationId;
    }

    override async parsePath(pathData: TrainPathData): Promise<TrainUrlParams> {
        const { origin, destination } = pathData;
        await this.searchLocations(origin);
        const originCode = this.findStationIdBaseOnCode(origin);
        const destinationCode = this.findStationIdBaseOnCode(destination);
        if (!originCode || !destinationCode) {
            throw new VerifyPathError(
                'origin or destination not found',
                404,
                false
            );
        }
        return {
            origin,
            destination,
            originCode,
            destinationCode,
            departureDate: pathData.departureDate
                ? parseDate(pathData.departureDate)!
                : dayConfig.gregory().add(1, 'day').toDate(),
            sellTypeCode: Number(pathData.sellTypeCode) as SellTypeCode,
            exclusive: pathData.exclusive === '1',
            adultCount: Number(pathData.adultCount) || 1,
            childCount: pathData?.childCount
                ? parseInt(pathData.childCount)
                : 0,
            infantCount: pathData?.infantCount
                ? parseInt(pathData.infantCount)
                : 0,
            returnDate: pathData.returnDate
                ? parseDate(pathData.returnDate)!
                : undefined,
        };
    }

    setSellTypeCode(code: SellTypeCode) {
        this.sellType =
            trainSellTypes.find(sellType => sellType.code === code) ||
            this.sellType;
        if (this.sellType.code === SellTypeCode.Car) {
            this.initPaxCounts = [
                {
                    code: PaxType.Adult,
                    title: 'دستگاه',
                    subtitle: '',
                    count: 1,
                },
            ];
        } else {
            this.initPaxCounts = [
                {
                    code: PaxType.Adult,
                    title: 'بزرگسال',
                    subtitle: 'بزرگتر از ۱۲ سال',
                    count: this.adultCount,
                },
                {
                    code: PaxType.Child,
                    title: 'کودک',
                    subtitle: 'بین ۲ الی ۱۲ سال',
                    count: this.childCount,
                },
            ];
        }
    }
}
